import { Loader, unit, Text, TextInput, Section, toast } from '@acast/decibel';
import {
  fetchShowAccessInfo,
  selectShowAccessLoadingStatus,
  selectShowAccessSettings,
  updateShowAccessInfo,
} from 'ducks/access';
import { addChange, clearChanges, selectChanges, selectHasChanges } from 'ducks/changes';
import T from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import Field from 'shared/components/field';
import RichTextEditor from 'shared/components/rich-text-editor';
import UploadableImage from 'shared/components/uploadable-image';
import styled from 'styled-components';

const DEFAULT_IMAGE_SOURCE = '/assets/images/default-show.png';
const getChangeKey = showId => `${showId}-private`;

const LoaderOverlay = styled.div`
  position: relative;
  opacity: ${props => (props.loading ? '0.5' : '1')};
  width: 100%;
  height: 100%;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  z-index: 100;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingInfo = styled.div`
  z-index: 999;
`;

const Row = styled.div`
  margin-bottom: ${unit(4)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const UploadableImageContainer = styled.div`
  border-radius: 2px;
  background-color: #f9fafb;
  border: solid 1px #a1a4aa;
`;

const UploadableImageBody = styled.div`
  max-width: 200px;
  margin: 0 auto;
`;

class ShowSettingsAccessForm extends React.Component {
  componentDidMount() {
    this.fetchAcastAccessShow();
  }

  componentDidUpdate(prevProps) {
    const { hasChanges } = this.props;

    if (hasChanges && !prevProps.hasChanges) {
      this.openToast();
    }

    if (!hasChanges && prevProps.hasChanges) {
      this.closeToast();
    }
  }

  componentWillUnmount() {
    this.closeToast();
    this.handleDiscardingChanges();
  }

  saveToastId = null;

  fetchAcastAccessShow = () => {
    const { onFetchShowAccessInfo, params } = this.props;

    onFetchShowAccessInfo(params.showId);
  };

  handleChange = key => value => {
    const { onAddChange, params } = this.props;
    const changesKey = getChangeKey(params.showId);

    onAddChange(changesKey, key, value);
  };

  handleSavingChanges = () => {
    const { show, changes, onUpdateShowAccessInfo, params } = this.props;
    const { title, description, image } = { ...show, ...changes };
    const payload = {
      title: title || null,
      description:
        description && description.replace(/(<([^>]+)>)/gi, '').trim() !== '' ? description : null,
      image: image || null,
    };

    onUpdateShowAccessInfo(params.showId, payload).then(() => {
      this.handleDiscardingChanges();
    });
  };

  handleDiscardingChanges = () => {
    const { onClearChanges, params } = this.props;
    const changesKey = getChangeKey(params.showId);

    onClearChanges(changesKey);
  };

  openToast = () => {
    if (this.saveToastId !== null) return;

    this.saveToastId = toast.secondary('You have unsaved changes. Do you want to save them?', {
      multiLine: true,
      position: 'bottom',
      actions: [
        { text: 'Save', onClick: this.handleSavingChanges },
        { text: 'Discard', onClick: this.handleDiscardingChanges },
      ],
    });
  };

  closeToast = () => {
    if (this.saveToastId === null) return;

    toast.close(this.saveToastId);
    this.saveToastId = null;
  };

  render() {
    const { loading, params, show, changes } = this.props;
    const { title, description, image } = { ...show, ...changes };
    return (
      <Section>
        <LoaderOverlay loading={loading}>
          {loading && (
            <LoaderWrapper>
              <LoadingInfo>
                <Loader width={26} />
              </LoadingInfo>
            </LoaderWrapper>
          )}
          <Text variant="h3">Private Show Details</Text>
          <Row>
            <Field subtitle="Min. size 1400x1400 px – Max size 3000x3000 px in .png or .jpg">
              <UploadableImageContainer>
                <UploadableImageBody>
                  <UploadableImage
                    entity={params.showId}
                    src={image || DEFAULT_IMAGE_SOURCE}
                    role="presentation"
                    onImageUploaded={this.handleChange('image')}
                    onImageRemoved={this.handleChange('image')}
                    enableRemove={!!image}
                  />
                </UploadableImageBody>
              </UploadableImageContainer>
            </Field>
          </Row>
          <Row>
            <Field disabled={loading} label="Private Show Title">
              <TextInput
                onChange={event => this.handleChange('title')(event.target.value)}
                value={title}
              />
            </Field>
          </Row>
          <Row>
            <Field disabled={loading} label="Private Show Description">
              <RichTextEditor onChange={this.handleChange('description')} value={description} />
            </Field>
          </Row>
        </LoaderOverlay>
      </Section>
    );
  }
}

ShowSettingsAccessForm.propTypes = {
  changes: T.shape({
    description: T.string,
    image: T.string,
    title: T.string,
  }),
  hasChanges: T.bool.isRequired,
  loading: T.bool.isRequired,
  onAddChange: T.func.isRequired,
  onClearChanges: T.func.isRequired,
  onFetchShowAccessInfo: T.func.isRequired,
  onUpdateShowAccessInfo: T.func.isRequired,
  params: T.shape({
    showId: T.string.isRequired,
  }).isRequired,
  show: T.shape({
    description: T.string,
    image: T.string,
    title: T.string,
  }).isRequired,
};

export default connect(
  (state, { params }) => {
    const changesKey = getChangeKey(params.showId);

    return {
      changes: selectChanges(changesKey)(state),
      hasChanges: selectHasChanges(changesKey)(state),
      loading: selectShowAccessLoadingStatus(state),
      show: selectShowAccessSettings(state),
    };
  },
  {
    onAddChange: addChange,
    onClearChanges: clearChanges,
    onFetchShowAccessInfo: fetchShowAccessInfo,
    onUpdateShowAccessInfo: updateShowAccessInfo,
  }
)(ShowSettingsAccessForm);
